import React, { forwardRef } from "react";
import { useSpotlight } from "../../lib/interactive";
import { cn } from "../../lib/utils";

/**
 * The one surface, in four tones. There is no second card component.
 *
 * `lit` opts a card into the pointer-tracked highlight — worth it on anything
 * you can click, noise on a block of text.
 */

const TONES = {
  glass: "glass text-fg",
  /**
   * Solid, for the one block per view that has to sit forward — opaque where
   * glass is translucent, so it reads as nearer.
   *
   * It follows the theme (`--c-solid`) rather than being fixed to carbon. An
   * always-dark card is a trap: everything inside it uses the theme text
   * tokens, so in daylight the card stayed black while its type turned dark
   * and the whole panel went unreadable.
   */
  solid: "bg-solid text-fg border border-edge shadow-brand",
  /* The loud one. Exactly one per screen. */
  accent: "bg-accent text-void border border-transparent shadow-volt",
  bare: "bg-transparent border-0 shadow-none",
};

const PADS = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
  lg: "p-6 desk:p-8",
};

const Card = forwardRef(function Card(
  { as: Tag = "div", tone = "glass", pad = "md", lit = false, className, children, ...rest },
  ref,
) {
  const spot = useSpotlight();
  const bare = tone === "bare";

  return (
    <Tag
      ref={ref ?? (lit ? spot : undefined)}
      className={cn(
        "relative overflow-hidden",
        bare ? "" : "rounded-brand",
        lit && "spot",
        TONES[tone],
        PADS[bare ? "none" : pad],
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

export default Card;

/** The mono all-caps eyebrow above almost every block. */
export function Kicker({ children, className, ...rest }) {
  return (
    <div className={cn("kicker mb-4", className)} {...rest}>
      {children}
    </div>
  );
}

/** Definition rows split by a hairline — the workhorse for any list of facts. */
export function Rows({ children, className }) {
  return <div className={cn("flex flex-col", className)}>{children}</div>;
}

export function Row({ index, children, meta, metaAccent, className }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-t border-hair py-3.5 text-[13.5px] first:border-t-0",
        className,
      )}
    >
      {index != null && <span className="kicker min-w-[34px] shrink-0 normal-case">{index}</span>}
      {/* min-w-0 or this refuses to shrink and pushes the meta off a phone. */}
      <span className="min-w-0 flex-1 font-semibold">{children}</span>
      {meta && (
        <span
          className={cn(
            "kicker ml-auto max-w-[45%] shrink-0 text-right break-words normal-case",
            metaAccent && "text-accent-text",
          )}
        >
          {meta}
        </span>
      )}
    </div>
  );
}

/** Mono 8.5px uppercase in a hairline pill. */
export function Chip({ tone = "default", className, children, ...rest }) {
  const tones = {
    default: "bg-surface-solid border-line text-fg",
    accent: "border-transparent bg-accent text-void",
    outline: "bg-transparent border-line text-muted",
  };
  return (
    <span
      className={cn(
        "kicker inline-flex items-center gap-1.5 rounded-full border px-3 py-2 leading-none",
        tones[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
